import { PricingCasePlan, PricingCaseSection, PricingPlan, PricingQACase, PricingRules, PricingSetupRow, PricingSheet } from './api';
import {
  casePricingEnabled, casePricingSectionConfirmed, caseSaleTotal, computeQASchedule, computeSchedule,
  confirmCasePricingSection, confirmPricingSection, createCasePricingPlan,
  DEFAULT_RULES, invalidatePricingSections, parseMoney, parseWeeks, planModeOf, pricingSectionConfirmed,
  round2, weeksFromDays, weeksStr,
} from './pricingMath';

export type PricingDurationUnit = 'weeks' | 'days';

const workingDays = (rules: PricingRules): number =>
  Number.isFinite(rules.week_days) && rules.week_days > 0 ? rules.week_days : 5;

/** Display units never change the canonical two-decimal working weeks. */
export function pricingDurationFromWeeks(weeks: number, unit: PricingDurationUnit,
  rules: PricingRules = DEFAULT_RULES): number {
  if (!Number.isFinite(weeks) || weeks < 0) return 0;
  return round2(round2(weeks) * (unit === 'days' ? workingDays(rules) : 1));
}

export function pricingDurationToWeeks(value: number, unit: PricingDurationUnit,
  rules: PricingRules = DEFAULT_RULES): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return round2(value / (unit === 'days' ? workingDays(rules) : 1));
}

/** Initial missing values may use the estimate. A deliberate zero stays zero,
 * so clearing an input cannot silently restore an old duration. */
export function initialCaseDevelopmentWeeks(row: PricingSetupRow, decision?: PricingCasePlan,
  rules: PricingRules = DEFAULT_RULES): number {
  if (decision?.dev_weeks != null) return pricingDurationToWeeks(decision.dev_weeks, 'weeks', rules);
  if (String(row.weeks ?? '').trim()) return round2(parseWeeks(row.weeks));
  return weeksFromDays(row.days ?? 0, workingDays(rules));
}

/** A display/explicit-opt-in projection, never an automatic saved migration.
 * createCasePricingPlan preserves existing global overrides, cost and prices.
 * Keep using the original sheet for saved-confirmation and legacy-cost state
 * until the author edits a case or confirms the projected development step. */
export function prepareCasePricingReview(sheet: PricingSheet,
  rules: PricingRules = DEFAULT_RULES): PricingSheet {
  // Match server normalizeSheet before taking review snapshots. The source
  // estimate and an explicitly cleared case decision are separate fields.
  const setup = sheet.setup.map(row => parseWeeks(row.weeks) <= 0 && typeof row.days === 'number' && row.days > 0
    ? { ...row, weeks: weeksStr(weeksFromDays(row.days, workingDays(rules))) } : row);
  const legacy = sheet.plan?.workflow_version !== 'lato-pricing-v2';
  let initial: PricingSheet = { ...sheet, setup };
  if (legacy) {
    // Historical scheduling is determined by row stages, even if an old mode
    // label disagrees with them. Projecting the new UI must not change it.
    initial = { ...initial, plan: { ...(sheet.plan ?? { qa_weeks: 0 }), mode: planModeOf(setup) } };
  }
  if (casePricingEnabled(sheet.plan)) {
    initial = { ...initial, plan: { ...sheet.plan!, case_pricing: sheet.plan!.case_pricing!.map(decision => {
      const row = setup.find(item => item.case === decision.case);
      return row && decision.dev_weeks == null
        ? { ...decision, dev_weeks: initialCaseDevelopmentWeeks(row, decision, rules) } : decision;
    }) } };
  }
  const plan = createCasePricingPlan(initial, rules);
  if (!legacy) return { ...initial, plan };
  const case_pricing = plan.case_pricing!.map((decision, index) => ({ ...decision,
    // Legacy inputs are the row fields themselves. In particular, a blank
    // price must not become an allocation of the other cases' agreed prices.
    dev_weeks: initialCaseDevelopmentWeeks(sheet.setup[index], undefined, rules),
    sale_price: parseMoney(sheet.setup[index].price),
  }));
  const projected = { ...plan, case_pricing };
  return { ...initial, plan: { ...projected,
    dev_weeks: computeSchedule(setup, projected).dev_weeks,
    sale_price: caseSaleTotal(projected),
  } };
}

/** Checks the complete table before stamping any case. The four global steps
 * still produce the exact per-case snapshots expected by the server. */
export function casePricingStepReady(sheet: PricingSheet, section: PricingCaseSection): boolean {
  if (!casePricingEnabled(sheet.plan) || !sheet.setup.length) return false;
  const decisions = sheet.plan!.case_pricing!;
  const names = sheet.setup.map(row => row.case);
  if (new Set(names).size !== names.length || decisions.length !== names.length
      || new Set(decisions.map(value => value.case)).size !== decisions.length) return false;
  if (!names.every(name => decisions.some(value => value.case === name))) return false;
  if (!decisions.every(value => Number.isFinite(value.dev_weeks) && value.dev_weeks > 0
      && value.dev_weeks <= 520)) return false;
  if (section === 'development') return true;
  return pricingSectionConfirmed(sheet, 'development') && pricingSectionConfirmed(sheet, 'qa')
    && decisions.every(value => casePricingSectionConfirmed(sheet, value.case, 'development')
      && Number.isInteger(value.dev_engineers) && value.dev_engineers >= 1 && value.dev_engineers <= 50
      && (value.dev_level === 'middle' || value.dev_level === 'senior')
      && typeof value.sale_price === 'number' && Number.isFinite(value.sale_price)
      && value.sale_price >= 0 && value.sale_price <= 1e9);
}

export function confirmAllCasePricingSections(sheet: PricingSheet, section: PricingCaseSection,
  rules: PricingRules = DEFAULT_RULES): PricingSheet {
  if (!casePricingEnabled(sheet.plan)) return confirmPricingSection(sheet, section, rules);
  if (!casePricingStepReady(sheet, section)) return sheet;
  let next = sheet;
  for (const row of sheet.setup) {
    if (!casePricingSectionConfirmed(next, row.case, section)) {
      next = confirmCasePricingSection(next, row.case, section, rules);
    }
  }
  return pricingSectionConfirmed(next, section) ? next : confirmPricingSection(next, section, rules);
}

/** QA remains a global decision even when its allowance is entered per case. */
export function qaReviewReady(sheet: PricingSheet): boolean {
  return computeQASchedule(sheet.setup, sheet.plan).complete;
}

function updateQAPlan(sheet: PricingSheet, plan: PricingPlan): PricingSheet {
  const qa = computeQASchedule(sheet.setup, plan);
  return invalidatePricingSections({ ...sheet,
    plan: Array.isArray(plan.qa_cases) && qa.complete ? { ...plan, qa_weeks: qa.weeks } : plan,
  }, 'qa');
}

/** Explicit opt-in only: do not invent a case split from one global value.
 * The old allowance stays visible until every case has an entered value. */
export function enableCaseQA(sheet: PricingSheet): PricingSheet {
  if (Array.isArray(sheet.plan?.qa_cases) || !sheet.setup.length) return sheet;
  const plan = sheet.plan ?? { mode: planModeOf(sheet.setup), qa_weeks: 0 };
  return updateQAPlan(sheet, { ...plan, qa_mode: 'sequential',
    qa_cases: sheet.setup.map((row, index) => ({ case: row.case, weeks: null, stage: index + 1 })),
  });
}

export function updateCaseQA(sheet: PricingSheet, caseName: string,
  patch: Partial<Pick<PricingQACase, 'weeks' | 'stage'>>): PricingSheet {
  if (!sheet.setup.some(row => row.case === caseName)) return sheet;
  const current = enableCaseQA(sheet);
  if (!current.plan?.qa_cases?.some(row => row.case === caseName)) return sheet;
  const weeks = patch.weeks === undefined ? undefined
    : typeof patch.weeks === 'number' && Number.isFinite(patch.weeks) ? round2(patch.weeks) : null;
  return updateQAPlan(current, { ...current.plan,
    ...(patch.stage !== undefined ? { qa_mode: 'custom' as const } : {}),
    qa_cases: current.plan.qa_cases.map(row => row.case !== caseName ? row : { ...row,
      ...(weeks !== undefined ? { weeks } : {}),
      ...(patch.stage !== undefined ? { stage: patch.stage } : {}),
    }),
  });
}

export function changeQAMode(sheet: PricingSheet, mode: 'sequential' | 'parallel'): PricingSheet {
  const current = enableCaseQA(sheet);
  if (!current.plan?.qa_cases) return sheet;
  return updateQAPlan(current, { ...current.plan, qa_mode: mode,
    qa_cases: current.plan.qa_cases.map(row => ({ ...row,
      stage: mode === 'parallel' ? 1 : sheet.setup.findIndex(item => item.case === row.case) + 1,
    })),
  });
}

/** Switching back keeps the latest complete allowance, not an older default. */
export function useGlobalQA(sheet: PricingSheet): PricingSheet {
  if (!sheet.plan || (!Array.isArray(sheet.plan.qa_cases) && sheet.plan.qa_mode === undefined)) return sheet;
  const qa = computeQASchedule(sheet.setup, sheet.plan);
  const plan = { ...sheet.plan };
  delete plan.qa_cases;
  delete plan.qa_mode;
  return updateQAPlan(sheet, { ...plan, qa_weeks: qa.complete ? qa.weeks : plan.qa_weeks });
}

export function confirmQAReview(sheet: PricingSheet, rules: PricingRules = DEFAULT_RULES): PricingSheet {
  if (!qaReviewReady(sheet) || !pricingSectionConfirmed(sheet, 'development')) return sheet;
  if (pricingSectionConfirmed(sheet, 'qa')) return sheet;
  return confirmPricingSection(sheet, 'qa', rules);
}
